import React from 'react';
import { Box, FormControl, InputLabel, MenuItem, Select } from '@mui/material';
import {
  AGENT_SCOPE_ALL,
  type AgentScopeAll,
  type AgentScopedListItem,
} from '../utils/agentSuggestAssertionsScope';
import { AgentInlineSelect } from './ai-agent-ui/index';

export type AgentSuggestAssertionsScopeFieldsProps = {
  collectionId: number | AgentScopeAll;
  requestId: number | AgentScopeAll;
  collections: AgentScopedListItem[];
  requests: AgentScopedListItem[];
  onCollectionChange: (id: number | AgentScopeAll) => void;
  onRequestChange: (id: number | AgentScopeAll) => void;
  disabled?: boolean;
  loadingCollections?: boolean;
  loadingRequests?: boolean;
  /** stack = full-width vertical fields (widget); row = horizontal wrap (AI Agent page). */
  layout?: 'stack' | 'row';
  /**
   * `inline` uses viewport-anchored menus (required in the help assistant strip).
   * `mui` is for the full AI Agent page only.
   */
  selectVariant?: 'mui' | 'inline';
};

const AgentSuggestAssertionsScopeFields: React.FC<AgentSuggestAssertionsScopeFieldsProps> = ({
  collectionId,
  requestId,
  collections,
  requests,
  onCollectionChange,
  onRequestChange,
  disabled = false,
  loadingCollections = false,
  loadingRequests = false,
  layout = 'row',
  selectVariant = layout === 'stack' ? 'inline' : 'mui',
}) => {
  const busy = disabled || loadingCollections || loadingRequests;
  const collectionValue = collectionId === AGENT_SCOPE_ALL ? AGENT_SCOPE_ALL : collectionId;
  const requestValue = requestId === AGENT_SCOPE_ALL ? AGENT_SCOPE_ALL : requestId;
  const isStack = layout === 'stack';
  const fieldSx = isStack
    ? { width: '100%' }
    : { width: '100%', minWidth: 0 };
  const requestFieldSx = isStack
    ? { width: '100%' }
    : { width: '100%', minWidth: 0 };

  const fields =
    selectVariant === 'inline' ? (
      <>
        <AgentInlineSelect
          id="agent-suggest-assertions-collection"
          label="Collection"
          value={collectionValue}
          disabled={busy}
          fullWidth={isStack}
          sx={fieldSx}
          options={[
            { value: AGENT_SCOPE_ALL, label: 'All Collections' },
            ...collections.map((c) => ({
              value: c.id,
              label: c.label ?? c.name,
              indent: c.depth ?? 0,
            })),
          ]}
          onChange={(raw) => {
            onCollectionChange(raw === AGENT_SCOPE_ALL ? AGENT_SCOPE_ALL : Number(raw));
          }}
        />
        <AgentInlineSelect
          id="agent-suggest-assertions-request"
          label="Request"
          value={requestValue}
          disabled={busy}
          fullWidth={isStack}
          sx={requestFieldSx}
          options={[
            { value: AGENT_SCOPE_ALL, label: 'All Requests' },
            ...requests.map((r) => ({
              value: r.id,
              label: r.name,
            })),
          ]}
          onChange={(raw) => {
            onRequestChange(raw === AGENT_SCOPE_ALL ? AGENT_SCOPE_ALL : Number(raw));
          }}
        />
      </>
    ) : (
      <>
        <FormControl size="small" fullWidth={isStack} sx={fieldSx} disabled={busy}>
          <InputLabel id="agent-suggest-assertions-collection-label">Collection</InputLabel>
          <Select
            labelId="agent-suggest-assertions-collection-label"
            label="Collection"
            value={collectionValue}
            onChange={(e) => {
              const raw = e.target.value;
              onCollectionChange(raw === AGENT_SCOPE_ALL ? AGENT_SCOPE_ALL : Number(raw));
            }}
          >
            <MenuItem value={AGENT_SCOPE_ALL}>All Collections</MenuItem>
            {collections.map((c) => (
              <MenuItem key={c.id} value={c.id} sx={{ pl: 1.5 + (c.depth ?? 0) * 1.5 }}>
                {c.label ?? c.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" fullWidth={isStack} sx={requestFieldSx} disabled={busy}>
          <InputLabel id="agent-suggest-assertions-request-label">Request</InputLabel>
          <Select
            labelId="agent-suggest-assertions-request-label"
            label="Request"
            value={requestValue}
            onChange={(e) => {
              const raw = e.target.value;
              onRequestChange(raw === AGENT_SCOPE_ALL ? AGENT_SCOPE_ALL : Number(raw));
            }}
          >
            <MenuItem value={AGENT_SCOPE_ALL}>All Requests</MenuItem>
            {requests.map((r) => (
              <MenuItem key={r.id} value={r.id}>
                {r.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </>
    );

  if (isStack) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: '100%', mb: 1 }}>
        {fields}
      </Box>
    );
  }

  return fields;
};

export default AgentSuggestAssertionsScopeFields;
